import type { FormApi } from '../../../form-hooks/useFormApi';
import type {
  ContentObject,
  ContentTypeDefinition,
  ContentTypePropertySchema,
  WithOptionalInternalProps,
} from '../../../lib/flotiq-client/types/api-response';

import deepEqual from 'deep-equal';

import { getContentType } from '../../../lib/flotiq-client';

export type SkippedContentTypeRefreshFieldReason =
  'missingField' | 'typeChanged' | 'arrayItemTypeChanged';

export type SkippedContentTypeRefreshField = {
  key: string;
  reason: SkippedContentTypeRefreshFieldReason;
};

export type ContentTypeRefreshSnapshot = {
  compatibleValues: Record<string, unknown>;
  skippedFields: SkippedContentTypeRefreshField[];
};

export type ContentTypeRefreshPlan = {
  nextContentType: ContentTypeDefinition;
  compatibleValues: Record<string, unknown>;
  skippedFields: SkippedContentTypeRefreshField[];
  incompatibleFields: SkippedContentTypeRefreshField[];
};

export const getContentTypeSchemaProperties = (
  contentType?: ContentTypeDefinition | null,
) => contentType?.schemaDefinition?.allOf?.[1]?.properties || {};

export const getSchemaFieldIncompatibilityReason = (
  oldFieldSchema: ContentTypePropertySchema,
  newFieldSchema?: ContentTypePropertySchema,
): SkippedContentTypeRefreshFieldReason | null => {
  if (!newFieldSchema) return 'missingField';

  if (oldFieldSchema.type !== newFieldSchema.type) return 'typeChanged';

  if (oldFieldSchema.type !== 'array') return null;

  if (!deepEqual(oldFieldSchema.items, newFieldSchema.items)) {
    return 'arrayItemTypeChanged';
  }

  return null;
};

export const getIncompatibleContentTypeFields = (
  oldContentType?: ContentTypeDefinition | null,
  newContentType?: ContentTypeDefinition | null,
): SkippedContentTypeRefreshField[] => {
  const oldSchema = getContentTypeSchemaProperties(oldContentType);
  const newSchema = getContentTypeSchemaProperties(newContentType);

  return Object.entries(oldSchema).reduce<SkippedContentTypeRefreshField[]>(
    (fields, [key, oldFieldSchema]) => {
      const incompatibilityReason = getSchemaFieldIncompatibilityReason(
        oldFieldSchema,
        newSchema[key],
      );

      if (incompatibilityReason) {
        fields.push({ key, reason: incompatibilityReason });
      }

      return fields;
    },
    [],
  );
};

export const getDirtyFieldsFromValues = (
  currentValues: Record<string, unknown>,
  initialValues: Record<string, unknown>,
) =>
  Object.keys(currentValues).filter(
    (key) => !deepEqual(currentValues[key], initialValues[key]),
  );

export const buildContentTypeRefreshSnapshot = ({
  currentValues,
  dirtyFields,
  oldContentType,
  newContentType,
}: {
  currentValues: Record<string, unknown>;
  dirtyFields: string[];
  oldContentType?: ContentTypeDefinition | null;
  newContentType?: ContentTypeDefinition | null;
}): ContentTypeRefreshSnapshot => {
  const oldSchema = getContentTypeSchemaProperties(oldContentType);
  const newSchema = getContentTypeSchemaProperties(newContentType);

  return dirtyFields.reduce<ContentTypeRefreshSnapshot>(
    (snapshot, key) => {
      const oldFieldSchema = oldSchema[key];

      if (!oldFieldSchema) return snapshot;

      const incompatibilityReason = getSchemaFieldIncompatibilityReason(
        oldFieldSchema,
        newSchema[key],
      );

      if (incompatibilityReason) {
        snapshot.skippedFields.push({ key, reason: incompatibilityReason });
        return snapshot;
      }

      if (key in currentValues) {
        snapshot.compatibleValues[key] = currentValues[key];
      }

      return snapshot;
    },
    { compatibleValues: {}, skippedFields: [] },
  );
};

const getContentObjectValues = (
  contentObject?: WithOptionalInternalProps<ContentObject> | null,
): Record<string, unknown> => {
  if (!contentObject) return {};

  const { id: _id, internal: _internal, ...rest } = contentObject;
  return rest as Record<string, unknown>;
};

/**
 * Fetches the latest CTD and computes what to keep vs. drop when refreshing the
 * open form.
 */
export const fetchContentTypeRefreshPlan = async ({
  jwt,
  space,
  contentTypeName,
  oldContentType,
  formState,
  contentObject,
}: {
  jwt?: string;
  space?: string;
  contentTypeName: string;
  oldContentType: ContentTypeDefinition;
  formState: FormApi;
  contentObject?: WithOptionalInternalProps<ContentObject> | null;
}): Promise<ContentTypeRefreshPlan | null> => {
  if (!jwt || !space) return null;

  const response = await getContentType(jwt, space, { contentTypeName });
  if (!response.ok) return null;

  const nextContentType = response.body;
  const currentValues = formState.getValues() as Record<string, unknown>;

  const dirtyFieldsFromForm = formState.getDirtyFields();
  const dirtyFields = dirtyFieldsFromForm.length
    ? dirtyFieldsFromForm
    : getDirtyFieldsFromValues(
        currentValues,
        getContentObjectValues(contentObject),
      );

  const { compatibleValues, skippedFields } = buildContentTypeRefreshSnapshot({
    currentValues,
    dirtyFields,
    oldContentType,
    newContentType: nextContentType,
  });

  return {
    nextContentType,
    compatibleValues,
    skippedFields,
    incompatibleFields: getIncompatibleContentTypeFields(
      oldContentType,
      nextContentType,
    ),
  };
};
